import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
import { FileText, Upload, Loader2, ChevronLeft, ChevronRight, Trash2, Eraser, Pencil } from 'lucide-react';
import { classroomApi } from '@/lib/classroomApi';
import { toast } from 'sonner';

// pdf.js worker — load from CDN; matches the bundled version
pdfjs.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.mjs`;

/**
 * Phase 3A-1 document viewer:
 *  - Tutor uploads PDF (Cloudinary signed upload via existing resource flow)
 *  - Shared page navigation (tutor controls; broadcast via Daily app messages)
 *  - Per-page annotation canvas (tutor draws strokes, broadcast live + persisted)
 *  - Students see annotations + page changes in real time
 */

const ANNOT_COLOR = '#E11D48';
const STROKE_WIDTH = 3;

const AnnotationCanvas = React.forwardRef(({ width, height, role, strokes, onAddStroke, onClear, eraser }, ref) => {
  const canvasRef = useRef(null);
  const drawingRef = useRef(null);

  // Redraw all strokes whenever they change or canvas resizes
  useEffect(() => {
    const c = canvasRef.current; if (!c) return;
    const ctx = c.getContext('2d');
    ctx.clearRect(0, 0, c.width, c.height);
    ctx.lineCap = 'round'; ctx.lineJoin = 'round';
    for (const s of strokes) {
      ctx.beginPath();
      ctx.strokeStyle = s.color || ANNOT_COLOR;
      ctx.lineWidth = (s.width || STROKE_WIDTH) * (width / 1000);
      const pts = s.points || [];
      for (let i = 0; i < pts.length; i++) {
        const [x, y] = pts[i];
        const sx = x * width; const sy = y * height;
        if (i === 0) ctx.moveTo(sx, sy); else ctx.lineTo(sx, sy);
      }
      ctx.stroke();
    }
  }, [strokes, width, height]);

  React.useImperativeHandle(ref, () => ({ clear: onClear }));

  const isTutor = role === 'tutor';
  const handleDown = (e) => {
    if (!isTutor) return;
    if (eraser) { onClear(); return; }
    const rect = e.currentTarget.getBoundingClientRect();
    const pt = e.touches?.[0] || e;
    drawingRef.current = { color: ANNOT_COLOR, width: STROKE_WIDTH, points: [[(pt.clientX - rect.left) / rect.width, (pt.clientY - rect.top) / rect.height]] };
  };
  const handleMove = (e) => {
    if (!drawingRef.current) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const pt = e.touches?.[0] || e;
    drawingRef.current.points.push([(pt.clientX - rect.left) / rect.width, (pt.clientY - rect.top) / rect.height]);
    // visual feedback during draw — redraw current stroke
    const c = canvasRef.current; if (!c) return;
    const ctx = c.getContext('2d');
    const s = drawingRef.current;
    const pts = s.points;
    if (pts.length < 2) return;
    ctx.beginPath();
    ctx.strokeStyle = s.color; ctx.lineWidth = s.width * (width / 1000);
    ctx.lineCap = 'round';
    const [x0, y0] = pts[pts.length - 2];
    const [x1, y1] = pts[pts.length - 1];
    ctx.moveTo(x0 * width, y0 * height);
    ctx.lineTo(x1 * width, y1 * height);
    ctx.stroke();
  };
  const handleUp = () => {
    if (!drawingRef.current) return;
    const s = drawingRef.current;
    drawingRef.current = null;
    if (s.points.length > 1) onAddStroke(s);
  };

  return (
    <canvas ref={canvasRef} width={width} height={height}
      className={`absolute inset-0 ${isTutor && !eraser ? 'cursor-crosshair' : eraser ? 'cursor-pointer' : 'pointer-events-none'}`}
      onMouseDown={handleDown} onMouseMove={handleMove} onMouseUp={handleUp} onMouseLeave={handleUp}
      onTouchStart={handleDown} onTouchMove={handleMove} onTouchEnd={handleUp}
      style={{ touchAction: 'none' }}
    />
  );
});
AnnotationCanvas.displayName = 'AnnotationCanvas';

const DocumentViewer = ({ doc, roomId, token, role, callObject }) => {
  const [pageNum, setPageNum] = useState(1);
  const [strokesByPage, setStrokesByPage] = useState({});  // {page: [strokes]}
  const [pageSize, setPageSize] = useState({ w: 800, h: 1000 });
  const [eraser, setEraser] = useState(false);
  const [pdfPageCount, setPdfPageCount] = useState(doc.page_count || 1);
  const [loadAttempt, setLoadAttempt] = useState(0);
  const [loadError, setLoadError] = useState(null);
  const containerRef = useRef(null);

  const isTutor = role === 'tutor';
  // Proxied PDF URL — backend streams the file with correct CORS + inline disposition.
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
  const proxyUrl = `${BACKEND_URL}/api/classroom/room/${roomId}/documents/${doc.document_id}/serve?t=${encodeURIComponent(token)}`;
  // Memoise so react-pdf doesn't refetch on every parent re-render.
  const pdfFile = React.useMemo(() => ({ url: proxyUrl }), [proxyUrl]);

  // Load saved annotations
  useEffect(() => {
    classroomApi.annotationsList(roomId, token, doc.document_id).then((r) => {
      const map = {};
      for (const a of r.items || []) map[a.page] = (map[a.page] || []).concat(a.annotations || []);
      setStrokesByPage(map);
    }).catch(() => {});
  }, [roomId, token, doc.document_id]);

  // Realtime: receive remote page-changes and remote strokes
  useEffect(() => {
    if (!callObject) return;
    const handler = (e) => {
      const m = e?.data; if (!m) return;
      if (m.kind === 'wh-doc-page' && m.document_id === doc.document_id) {
        setPageNum(m.page);
      }
      if (m.kind === 'wh-doc-stroke' && m.document_id === doc.document_id) {
        setStrokesByPage((prev) => ({ ...prev, [m.page]: [...(prev[m.page] || []), m.stroke] }));
      }
      if (m.kind === 'wh-doc-clear' && m.document_id === doc.document_id) {
        setStrokesByPage((prev) => ({ ...prev, [m.page]: [] }));
      }
    };
    callObject.on('app-message', handler);
    return () => { try { callObject.off('app-message', handler); } catch (_e) {} };
  }, [callObject, doc.document_id]);

  const setPage = (n) => {
    const p = Math.max(1, Math.min(pdfPageCount, n));
    setPageNum(p);
    if (isTutor) {
      try { callObject?.sendAppMessage({ kind: 'wh-doc-page', document_id: doc.document_id, page: p }); } catch (_e) {}
    }
  };

  const persistPage = useCallback((page, strokes) => {
    classroomApi.annotationsSave(roomId, token, doc.document_id, page, strokes).catch(() => {});
  }, [roomId, token, doc.document_id]);

  const addStroke = (stroke) => {
    setStrokesByPage((prev) => {
      const next = { ...prev, [pageNum]: [...(prev[pageNum] || []), stroke] };
      persistPage(pageNum, next[pageNum]);
      return next;
    });
    try { callObject?.sendAppMessage({ kind: 'wh-doc-stroke', document_id: doc.document_id, page: pageNum, stroke }); } catch (_e) {}
  };
  const clearPage = () => {
    setStrokesByPage((prev) => ({ ...prev, [pageNum]: [] }));
    persistPage(pageNum, []);
    try { callObject?.sendAppMessage({ kind: 'wh-doc-clear', document_id: doc.document_id, page: pageNum }); } catch (_e) {}
  };

  const onLoad = (pdf) => { setPdfPageCount(pdf.numPages || 1); console.info('[winhouse-pdf] loaded', { document_id: doc.document_id, pages: pdf.numPages }); };
  const onRender = (page) => {
    setPageSize({ w: page.width, h: page.height });
    console.info('[winhouse-pdf] rendered page', { document_id: doc.document_id, page: pageNum, w: page.width });
  };

  // measure container width to scale PDF
  const [pageWidth, setPageWidth] = useState(700);
  useEffect(() => {
    const m = () => { if (containerRef.current) setPageWidth(Math.min(900, containerRef.current.offsetWidth - 24)); };
    m(); window.addEventListener('resize', m); return () => window.removeEventListener('resize', m);
  }, []);

  return (
    <div className="flex flex-col h-full bg-[#FAF9F6]" ref={containerRef}>
      <div className="flex items-center justify-between p-3 border-b border-gray-100 bg-white">
        <div className="flex items-center gap-2">
          <button onClick={() => setPage(pageNum - 1)} disabled={pageNum <= 1 || !isTutor}
            className="h-8 w-8 rounded-lg border border-gray-200 disabled:opacity-30 flex items-center justify-center"
            data-testid="doc-prev"><ChevronLeft className="h-4 w-4" /></button>
          <div className="text-xs font-semibold text-gray-700">Page {pageNum} / {pdfPageCount}</div>
          <button onClick={() => setPage(pageNum + 1)} disabled={pageNum >= pdfPageCount || !isTutor}
            className="h-8 w-8 rounded-lg border border-gray-200 disabled:opacity-30 flex items-center justify-center"
            data-testid="doc-next"><ChevronRight className="h-4 w-4" /></button>
        </div>
        {isTutor && (
          <div className="flex items-center gap-2">
            <button onClick={() => setEraser(false)}
              className={`h-8 px-3 rounded-lg text-[11px] uppercase tracking-wider font-bold flex items-center gap-1 ${!eraser ? 'bg-red-700 text-white' : 'bg-gray-100 text-gray-600'}`}>
              <Pencil className="h-3 w-3" /> Pen
            </button>
            <button onClick={() => setEraser(true)}
              className={`h-8 px-3 rounded-lg text-[11px] uppercase tracking-wider font-bold flex items-center gap-1 ${eraser ? 'bg-red-700 text-white' : 'bg-gray-100 text-gray-600'}`}>
              <Eraser className="h-3 w-3" /> Clear page
            </button>
          </div>
        )}
      </div>
      <div className="flex-1 overflow-y-auto p-3 flex items-start justify-center" data-testid="doc-pdf-area">
        <div className="relative shadow-sm" style={{ width: pageWidth }}>
          <Document
            key={`${doc.document_id}-${loadAttempt}`}
            file={pdfFile}
            onLoadSuccess={(pdf) => { setLoadError(null); onLoad(pdf); }}
            onLoadError={(err) => { console.error('[winhouse-pdf] load error', { document_id: doc.document_id, filename: doc.filename, error: err?.message }); setLoadError(err?.message || 'Unable to load PDF.'); }}
            loading={<div className="p-10 text-center text-gray-500 text-xs" data-testid="doc-loading"><Loader2 className="h-5 w-5 animate-spin inline mr-2" />Loading document…</div>}
            error={
              <div className="p-8 text-center bg-red-50 border border-red-200 rounded-2xl m-4" data-testid="doc-error">
                <div className="text-sm font-bold text-red-900 mb-1">We couldn't render this document</div>
                <div className="text-[11px] text-red-700 mb-4">{loadError || 'PDF.js was unable to parse the file.'}</div>
                <div className="flex justify-center gap-2 flex-wrap">
                  <button onClick={() => setLoadAttempt((n) => n + 1)} className="h-9 px-4 rounded-full bg-red-700 hover:bg-red-800 text-white text-[11px] font-bold uppercase tracking-wider" data-testid="doc-retry">Retry</button>
                  <a href={proxyUrl} target="_blank" rel="noreferrer" className="h-9 px-4 rounded-full bg-white border border-gray-200 text-[11px] font-bold uppercase tracking-wider inline-flex items-center" data-testid="doc-open-new-tab">Open in new tab</a>
                </div>
              </div>
            }>
            <Page pageNumber={pageNum} width={pageWidth} onRenderSuccess={onRender}
                  renderTextLayer={false} renderAnnotationLayer={false} />
          </Document>
          <AnnotationCanvas
            width={pageSize.w || pageWidth} height={pageSize.h || pageWidth * 1.3}
            role={role} strokes={strokesByPage[pageNum] || []}
            onAddStroke={addStroke} onClear={clearPage} eraser={eraser}
          />
        </div>
      </div>
    </div>
  );
};

const DocumentsPanel = ({ roomId, token, role, callObject }) => {
  const [docs, setDocs] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);

  const isTutor = role === 'tutor';
  const refresh = useCallback(async () => {
    try {
      const r = await classroomApi.documentsList(roomId, token);
      setDocs(r.items || []);
      setActiveId((prev) => prev || r.items?.[0]?.document_id || null);
    } catch (_e) {}
  }, [roomId, token]);
  useEffect(() => { refresh(); }, [refresh]);

  useEffect(() => {
    if (!callObject) return;
    const handler = (e) => {
      const m = e?.data; if (!m) return;
      if (m.kind === 'wh-docs-refresh') refresh();
      if (m.kind === 'wh-active-doc' && m.document_id) setActiveId(m.document_id);
    };
    callObject.on('app-message', handler);
    return () => { try { callObject.off('app-message', handler); } catch (_e) {} };
  }, [callObject, refresh]);

  const upload = async (file) => {
    if (file.size > 200 * 1024 * 1024) { toast.error('Max 200 MB'); return; }
    setUploading(true); setProgress(0);
    try {
      const sig = await classroomApi.documentSign(roomId, token);
      const fd = new FormData();
      fd.append('file', file);
      fd.append('api_key', sig.api_key);
      fd.append('timestamp', sig.timestamp);
      fd.append('signature', sig.signature);
      fd.append('folder', sig.folder);
      const result = await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest(); xhr.open('POST', sig.upload_url);
        xhr.upload.onprogress = (e) => { if (e.lengthComputable) setProgress(Math.round((e.loaded / e.total) * 100)); };
        xhr.onload = () => { if (xhr.status >= 200 && xhr.status < 300) resolve(JSON.parse(xhr.responseText)); else reject(new Error('Upload failed')); };
        xhr.onerror = () => reject(new Error('Network error'));
        xhr.send(fd);
      });
      const saved = await classroomApi.documentConfirm(roomId, token, {
        filename: file.name,
        secure_url: result.secure_url,
        public_id: result.public_id,
        size_bytes: file.size,
        page_count: result.pages || 1,
        mime_type: file.type || 'application/pdf',
      });
      setDocs((d) => [saved, ...d]);
      setActiveId(saved.document_id);
      try { callObject?.sendAppMessage({ kind: 'wh-docs-refresh' }); callObject?.sendAppMessage({ kind: 'wh-active-doc', document_id: saved.document_id }); } catch (_e) {}
      toast.success('Document ready');
    } catch (e) { toast.error(e.message || 'Upload failed'); }
    finally { setUploading(false); setProgress(0); }
  };

  const remove = async (id) => {
    if (!window.confirm('Remove this document and all annotations?')) return;
    try {
      await classroomApi.documentDelete(roomId, token, id);
      setDocs((d) => d.filter((x) => x.document_id !== id));
      if (activeId === id) setActiveId(docs.find((x) => x.document_id !== id)?.document_id || null);
      try { callObject?.sendAppMessage({ kind: 'wh-docs-refresh' }); } catch (_e) {}
    } catch (e) { toast.error(e.message || 'Delete failed'); }
  };

  const active = docs.find((d) => d.document_id === activeId);

  return (
    <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden flex flex-col" data-testid="classroom-documents" style={{ minHeight: 560 }}>
      <div className="px-4 py-2 border-b border-gray-100 bg-white flex items-center gap-2 flex-wrap">
        <FileText className="h-4 w-4 text-red-700" />
        <div className="text-xs uppercase tracking-wider text-gray-500 font-semibold">Documents</div>
        <div className="flex items-center gap-1.5 flex-wrap" data-testid="doc-tabs">
          {docs.map((d) => (
            <button key={d.document_id} onClick={() => { setActiveId(d.document_id); if (isTutor) try { callObject?.sendAppMessage({ kind: 'wh-active-doc', document_id: d.document_id }); } catch (_e) {} }}
              className={`h-8 px-3 rounded-lg text-xs font-semibold uppercase tracking-wider truncate max-w-[160px] ${activeId === d.document_id ? 'bg-red-700 text-white' : 'bg-gray-100 hover:bg-gray-200 text-gray-700'}`}
              data-testid={`doc-tab-${d.document_id}`}>{d.filename}</button>
          ))}
        </div>
        <div className="ml-auto flex items-center gap-2">
          {isTutor && (
            <label className="cursor-pointer h-8 px-3 rounded-lg bg-black text-white text-[11px] uppercase tracking-wider font-bold flex items-center gap-1.5"
                   data-testid="doc-upload">
              {uploading ? <Loader2 className="h-3 w-3 animate-spin" /> : <Upload className="h-3 w-3" />}
              {uploading ? `${progress}%` : 'Upload PDF'}
              <input type="file" accept=".pdf,application/pdf" className="hidden"
                onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; if (f) upload(f); }}
                disabled={uploading} />
            </label>
          )}
          {isTutor && active && (
            <button onClick={() => remove(active.document_id)}
              className="h-8 w-8 rounded-lg border border-gray-200 text-red-700 hover:bg-red-50 flex items-center justify-center">
              <Trash2 className="h-3 w-3" />
            </button>
          )}
        </div>
      </div>
      {uploading && <div className="h-1 bg-gray-100"><div className="h-full bg-red-600 transition-all" style={{ width: `${progress}%` }} /></div>}
      <div className="flex-1 overflow-hidden">
        {active ? (
          <DocumentViewer doc={active} roomId={roomId} token={token} role={role} callObject={callObject} />
        ) : (
          <div className="h-full flex items-center justify-center text-xs text-gray-400 p-8 text-center">
            {isTutor ? 'Upload a PDF to start sharing with your student' : 'Your tutor hasn\'t shared a document yet'}
          </div>
        )}
      </div>
    </div>
  );
};

export default DocumentsPanel;
